import React from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';

import {
  useActiveSettlementMethodQuery,
  useBillStatementsQuery,
} from 'apollo/queries/autogenerate/hooks';
import {
  AccountAvailability,
  AutoDebitSettlementMethodStatus,
  BillStatementStatusV2,
} from 'apollo/queries/autogenerate/schemas';
import { getBillDueDate } from 'shared/modules/bill/utils/get-bill-due-date';
import { getOldestOutstandingBill } from 'shared/modules/bill/utils/get-oldest-outstanding-bill';
import colors from 'shared/modules/common/constants/colors';
import { formatMonthDate } from 'shared/utils/date';
import { AutoDebitSection } from 'web/screens/Authenticated/Account/PaymentDetails/AutoDebitSection';
import { CombiniSection } from 'web/screens/Authenticated/Account/PaymentDetails/CombiniSection';

type Props = {
  billId?: string | null;
};

export default function PaymentDetails({ billId }: Props) {
  const { data } = useBillStatementsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
  });

  function getBillDetails() {
    // when user navigates to account screen directly from left menu display oldestOutstandingBill details
    if (billId === null || billId === undefined) {
      const oldestBill = getOldestOutstandingBill(data?.billStatements);

      if (oldestBill) {
        return {
          dueAt: oldestBill.dueAt,
          status: oldestBill.statusV2,
          billingCycle: oldestBill.billingCycle,
          id: oldestBill.id,
        };
      }
    }

    // when user navigates to account screen from bill card on home screen (overdue bill and outstanding bill)
    const bill = data?.billStatements.find(
      (billStatement) => billStatement.id === billId
    );

    if (billId && bill) {
      return {
        dueAt: bill.dueAt,
        status: bill.statusV2,
        billingCycle: bill.billingCycle,
        id: bill.id,
      };
    }

    // when user navigates to account screen from bill card on home screen (upcoming bill). Bill id passed as parameter is upcoming string so no bill will be found in above condition. In that we will get upcoming (placeholder) bill details.

    const today = new Date();
    const placeHolderDueAt = new Date();

    // This order is important
    // Set the date to 10th first then set the month
    // If we don't do it that way, this can cause an issue on months with only 30 days and Feb
    // Where JS will roll over to the month if that month has less days
    // Ex. Setting Sept 31 will roll over to Oct 1 since Sept only has 30 days
    placeHolderDueAt.setDate(10);
    placeHolderDueAt.setMonth(today.getMonth() + 1);

    return {
      dueAt: placeHolderDueAt,
      status: BillStatementStatusV2.OUTSTANDING,
      billingCycle: formatMonthDate(today.toDateString(), 'YYYY-MM'),
      id: '',
    };
  }

  //billId and id are different. billId is the parameter from URL but id is actual id of bill for which we are displaying details. In case of upcoming bill billId is upcoming but id is ''
  const { dueAt, status, billingCycle, id } = getBillDetails();

  const { data: activeSettlementMethod } = useActiveSettlementMethodQuery({
    variables: {
      billingCycle,
    },
    fetchPolicy: 'cache-and-network',
  });

  const combini =
    activeSettlementMethod?.activeSettlementMethod.settlementMethods.combini;

  const autoDebit =
    activeSettlementMethod?.activeSettlementMethod.settlementMethods.autoDebit;

  const isAccountAvailable =
    autoDebit?.accountAvailability !== AccountAvailability.UNAVAILABLE;

  const pullDate = getBillDueDate(true, dueAt, activeSettlementMethod);

  const isBillUpcoming =
    id === '' && status === BillStatementStatusV2.OUTSTANDING;

  const isBillOverdue = status === BillStatementStatusV2.OVERDUE;

  const nextAutoDebitByDate = activeSettlementMethod
    ? formatMonthDate(
        activeSettlementMethod.activeSettlementMethod.settlementMethods
          .autoDebit.nextAutoDebitByDate,
        'M月D日'
      )
    : '';

  const isAutoDebitActive =
    autoDebit?.status === AutoDebitSettlementMethodStatus.ACTIVE;

  const isBillGenerated = status === BillStatementStatusV2.OUTSTANDING && !!id;

  const displayAlert =
    isAutoDebitActive &&
    autoDebit?.accountAvailability !== AccountAvailability.AVAILABLE &&
    isBillGenerated;

  return (
    <View
      style={{ flexDirection: isAutoDebitActive ? 'column-reverse' : 'column' }}
    >
      {/* Combini section */}
      <CombiniSection
        isAutoDebitActive={isAutoDebitActive}
        billingCycle={billingCycle}
        isBillGenerated={isBillGenerated}
        nextAutoDebitByDate={nextAutoDebitByDate}
        settlementPeriodEnd={combini?.settlementPeriodEnd}
      />

      {/* Auto-debit section */}
      <AutoDebitSection
        autoDebitStatus={autoDebit?.status}
        isBillUpcoming={isBillUpcoming}
        nextAutoDebitByDate={nextAutoDebitByDate}
        pullDate={pullDate}
        isBillOverdue={isBillOverdue}
        isBillGenerated={isBillGenerated}
        isAccountAvailable={isAccountAvailable}
        billingCycle={billingCycle}
      />
      {displayAlert && (
        <View style={styles.container}>
          <Image
            style={styles.icon}
            source={require('shared/assets/images/bill/circled-statement-overdue@3x.png')}
          />
          <Text style={styles.alertText}>
            既に確定済みの前月ご利用分のご請求は、ご登録いただいていた銀行口座から引き落とされます。
          </Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  alertText: {
    fontSize: 13,
    lineHeight: 16,
    color: colors.red,
  },
  container: {
    backgroundColor: colors.white,
    padding: 24,
    borderRadius: 24,
    marginBottom: 24,
    flexDirection: 'row',
  },
  icon: {
    height: 20,
    width: 20,
    marginRight: 16,
  },
});
